import { motion } from "framer-motion";
import { Lock, ArrowRight, Sparkles } from "lucide-react";
import { analytics } from "@/lib/analytics";

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, delay, ease: [0.16, 1, 0.3, 1] },
});

/**
 * Investor Teaser — replaces the previous InvestorPitch on public landing.
 * Reveals nothing concrete. Drives accredited investors into the application
 * flow — full pilot economics, ask, and live demo are released only post-approval.
 */
export function InvestorTeaser({ openModal }) {
  return (
    <section
      className="relative py-24 md:py-32 overflow-hidden"
      id="investors"
      data-testid="investor-teaser-section"
    >
      {/* Premium backdrop — radial glow + grid */}
      <div className="absolute inset-0 [background-image:radial-gradient(circle_at_50%_50%,rgba(34,197,94,0.10),transparent_60%)]" />
      <div className="absolute inset-0 opacity-[0.04] [background-image:linear-gradient(rgba(34,197,94,0.4)_1px,transparent_1px),linear-gradient(90deg,rgba(34,197,94,0.4)_1px,transparent_1px)] [background-size:80px_80px]" />

      <div className="relative max-w-[1200px] mx-auto px-6 text-center">
        <motion.div {...fade(0)} className="inline-flex items-center gap-2 px-4 py-1.5 border border-[#22C55E]/30 bg-[#22C55E]/5 backdrop-blur-sm mb-6">
          <Lock className="w-3 h-3 text-[#22C55E]" />
          <span className="text-[#22C55E] text-[10px] font-semibold tracking-[0.3em] uppercase">By Invitation · Confidential</span>
        </motion.div>

        <motion.h2 {...fade(0.05)} className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] text-white max-w-3xl mx-auto">
          For investors building the next decade of <br className="hidden md:block" />
          <span className="text-[#22C55E]">trade infrastructure.</span>
        </motion.h2>

        <motion.p {...fade(0.1)} className="mt-6 text-[#C5D5CC] text-base lg:text-lg max-w-2xl mx-auto leading-relaxed">
          We're building the structured execution layer for the world's most fragmented $8T+ market.
          Pilot economics, full traction model, and a live walkthrough of the operating infrastructure
          are released only to approved investors.
        </motion.p>

        {/* Three locked teaser tiles — visible but blurred */}
        <motion.div {...fade(0.18)} className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-3 max-w-3xl mx-auto" data-testid="investor-locked-tiles">
          {[
            { label: "Pilot Economics", value: "•••" },
            { label: "5-Year Scale Model", value: "•••" },
            { label: "Live Operating Demo", value: "•••" },
          ].map((t, i) => (
            <div key={i} className="relative border border-[#1A2E22] bg-[#09120D] p-6 overflow-hidden">
              <div className="absolute inset-0 backdrop-blur-[2px] bg-[#050A07]/60" />
              <div className="relative">
                <Lock className="w-4 h-4 text-[#22C55E]/50 mb-3 mx-auto" />
                <p className="font-['Outfit'] text-3xl font-bold text-[#22C55E]/40 tracking-[0.2em]">{t.value}</p>
                <p className="text-[#A1B2A8] text-[10px] uppercase tracking-wider mt-2">{t.label}</p>
              </div>
            </div>
          ))}
        </motion.div>

        <motion.div {...fade(0.25)} className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-3">
          <button
            onClick={() => { analytics.ctaClicked("Apply as Investor", "investor-teaser"); openModal("investor"); }}
            className="bg-[#22C55E] text-[#050A07] hover:bg-[#1CAB4D] px-7 py-3.5 font-['Outfit'] font-semibold text-sm flex items-center gap-2 transition-all hover:gap-3"
            data-testid="investor-teaser-apply-btn"
          >
            <Sparkles className="w-4 h-4" /> Apply for Confidential Access <ArrowRight className="w-4 h-4" />
          </button>
        </motion.div>

        <motion.p {...fade(0.3)} className="mt-6 text-[#A1B2A8]/50 text-[10px] uppercase tracking-[0.25em]">
          Approved investors receive a personalized demo link · 24h review
        </motion.p>
      </div>
    </section>
  );
}
